import { useState } from 'react';
import { HelpCircle, X, ChevronDown, ChevronUp, Send, Clock, Mail, Download } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { usePwaInstall } from './PwaInstall';

const FAQS = [
  {
    q: 'Perche scegliere AeroDeals?',
    a: 'Siamo la piattaforma ideale per chi cerca un modo semplice, sicuro ed efficace per vendere o comprare un ultraleggero, aereo od elicottero.',
  },
  {
    q: 'Come metto l\'annuncio in evidenza?',
    a: 'Seleziona l\'opzione "Annuncio in Evidenza" durante la pubblicazione per assicurare la massima visibilità sulla piattaforma.',
  },
  {
    q: 'Come rendo il mio annuncio più visibile?',
    a: '1. Scatta e carica 10 foto in alta qualità di esterni ed interni, possibilmente in formato orizzontale.\n2. Compila ogni informazione in maniera accurata, scrivendo un\'estesa descrizione.\n3. Rispondi in tempi brevi ad utenti interessati, in modo cordiale e disponibile.\n4. Aggiungi l\'opzione Annuncio in Evidenza per la massima visibilità.',
  },
  {
    q: 'In quanto tempo vendero il mio velivolo?',
    a: 'Sebbene ogni modello susciti un diverso interesse, una tempistica media stimata per la vendita del proprio ultraleggero, aereo od elicottero e di circa 6 mesi. AeroDeals garantisce esclusivamente la visibilità online del proprio velivolo, ottimizzando al meglio l\'annuncio online.',
  },
  {
    q: 'Il costruttore non è presente in lista',
    a: 'Qualora durante la pubblicazione del tuo annuncio non dovessi trovare il Costruttore in lista, seleziona "OTHERS": lo aggiungeremo in fase di revisione.',
  },
];

export default function SupportWidget() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('faq');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [form, setForm] = useState({ nome: '', cognome: '', email: '', oggetto: '', messaggio: '' });
  const [sent, setSent] = useState(false);
  const { canInstall, install } = usePwaInstall();

  const toggleFaq = (i) => setExpandedFaq(expandedFaq === i ? null : i);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => { setSent(false); setForm({ nome: '', cognome: '', email: '', oggetto: '', messaggio: '' }); }, 3000);
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setOpen(!open)}
        className={`fixed bottom-20 right-4 z-[55] w-14 h-14 flex items-center justify-center shadow-2xl shadow-black/50 transition-all duration-300 ${
          open ? 'bg-zinc-800 border border-zinc-700 rotate-0' : 'bg-[#C5A059] hover:bg-[#D4B271] rotate-0'
        }`}
        data-testid="support-widget-btn"
      >
        {open ? <X size={20} className="text-white" /> : <HelpCircle size={22} className="text-black" />}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-36 right-4 z-[55] w-[360px] max-h-[520px] bg-zinc-900 border border-zinc-800 shadow-2xl shadow-black/60 flex flex-col animate-fade-in" data-testid="support-panel">
          {/* Header */}
          <div className="p-4 border-b border-zinc-800">
            <h3 className="text-base font-medium text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Supporto AeroDeals</h3>
            <div className="flex items-center gap-2 mt-1">
              <Clock size={12} className="text-[#C5A059]" />
              <span className="text-[11px] text-zinc-500">Lun - Ven, 09:00 - 18:00</span>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-zinc-800">
            <button
              onClick={() => setTab('faq')}
              className={`flex-1 py-2.5 text-xs font-medium transition-colors ${tab === 'faq' ? 'text-[#C5A059] border-b-2 border-[#C5A059]' : 'text-zinc-500 hover:text-zinc-300'}`}
              data-testid="support-tab-faq"
            >
              FAQ
            </button>
            <button
              onClick={() => setTab('contact')}
              className={`flex-1 py-2.5 text-xs font-medium transition-colors ${tab === 'contact' ? 'text-[#C5A059] border-b-2 border-[#C5A059]' : 'text-zinc-500 hover:text-zinc-300'}`}
              data-testid="support-tab-contact"
            >
              Contattaci
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {tab === 'faq' && (
              <div className="p-3 space-y-1" data-testid="support-faq-list">
                {FAQS.map((faq, i) => (
                  <div key={i} className="border border-zinc-800 bg-zinc-900/50">
                    <button
                      onClick={() => toggleFaq(i)}
                      className="w-full flex items-center justify-between p-3 text-left"
                      data-testid={`faq-toggle-${i}`}
                    >
                      <span className="text-xs font-medium text-zinc-200 pr-4">{faq.q}</span>
                      {expandedFaq === i ? <ChevronUp size={14} className="text-[#C5A059] shrink-0" /> : <ChevronDown size={14} className="text-zinc-600 shrink-0" />}
                    </button>
                    {expandedFaq === i && (
                      <div className="px-3 pb-3 text-[11px] text-zinc-400 leading-relaxed whitespace-pre-line" data-testid={`faq-answer-${i}`}>
                        {faq.a}
                      </div>
                    )}
                  </div>
                ))}

                {/* Install App */}
                {canInstall && (
                  <div className="mt-3 p-3 bg-[#C5A059]/5 border border-[#C5A059]/20">
                    <button onClick={install} className="w-full flex items-center gap-3 text-left" data-testid="support-install-app">
                      <div className="w-8 h-8 bg-[#C5A059]/15 flex items-center justify-center shrink-0">
                        <Download size={14} className="text-[#C5A059]" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-white">Installa AeroDeals</p>
                        <p className="text-[10px] text-zinc-400">Aggiungi alla schermata Home</p>
                      </div>
                    </button>
                  </div>
                )}

                {/* Email fallback */}
                <div className="mt-3 p-3 bg-zinc-800/50 border border-zinc-800 text-center">
                  <p className="text-[11px] text-zinc-500 mb-1">Non trovi la risposta?</p>
                  <a href="mailto:info@aerodeals.it" className="text-xs text-[#C5A059] hover:underline flex items-center justify-center gap-1">
                    <Mail size={12} /> info@aerodeals.it
                  </a>
                </div>
              </div>
            )}

            {tab === 'contact' && (
              <div className="p-4" data-testid="support-contact-form">
                {sent ? (
                  <div className="text-center py-8">
                    <div className="w-10 h-10 bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-3">
                      <Send size={16} className="text-emerald-400" />
                    </div>
                    <p className="text-sm text-white font-medium">Messaggio inviato!</p>
                    <p className="text-[11px] text-zinc-500 mt-1">Ti risponderemo al più presto.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-zinc-500 text-[10px] mb-1 block">Nome *</Label>
                        <Input
                          value={form.nome}
                          onChange={e => setForm(p => ({ ...p, nome: e.target.value }))}
                          required
                          className="bg-zinc-800 border-zinc-700 text-white h-8 text-xs"
                          data-testid="support-input-nome"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-500 text-[10px] mb-1 block">Cognome *</Label>
                        <Input
                          value={form.cognome}
                          onChange={e => setForm(p => ({ ...p, cognome: e.target.value }))}
                          required
                          className="bg-zinc-800 border-zinc-700 text-white h-8 text-xs"
                          data-testid="support-input-cognome"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-zinc-500 text-[10px] mb-1 block">Email *</Label>
                      <Input
                        type="email"
                        value={form.email}
                        onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                        required
                        className="bg-zinc-800 border-zinc-700 text-white h-8 text-xs"
                        data-testid="support-input-email"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-500 text-[10px] mb-1 block">Oggetto *</Label>
                      <Input
                        value={form.oggetto}
                        onChange={e => setForm(p => ({ ...p, oggetto: e.target.value }))}
                        required
                        className="bg-zinc-800 border-zinc-700 text-white h-8 text-xs"
                        data-testid="support-input-oggetto"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-500 text-[10px] mb-1 block">Messaggio</Label>
                      <Textarea
                        value={form.messaggio}
                        onChange={e => setForm(p => ({ ...p, messaggio: e.target.value }))}
                        className="bg-zinc-800 border-zinc-700 text-white min-h-[60px] text-xs"
                        data-testid="support-input-messaggio"
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-[#C5A059] text-black py-2 text-xs font-medium hover:bg-[#D4B271] transition-colors flex items-center justify-center gap-2"
                      data-testid="support-submit-btn"
                    >
                      <Send size={12} /> Invia
                    </button>

                    <p className="text-[10px] text-zinc-600 text-center mt-2">
                      Oppure scrivi a <a href="mailto:info@aerodeals.it" className="text-[#C5A059]">info@aerodeals.it</a>
                    </p>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
